/**
 * PageShell — the ONE place page chrome is composed (F4).
 *
 * THE DEFECT THIS CLOSES. Chrome — the navbar, the desktop `Footer`, the
 * mobile `MobileBottomNav` and the `pb-16` spacer that nav owes — was
 * hand-composed in ELEVEN layout modules, and no two agreed. Measured on the
 * hermetic fleet at 393x852: the account listing OVERVIEW and the section
 * EDITOR both rendered the public footer AND the tab bar; the listings hub
 * paid a `pb-16 md:pb-0` spacer for a nav it never rendered; `estate-detail`
 * dropped the footer its siblings keep. Every one of those is the same bug —
 * a policy nobody wrote down, restated eleven times.
 *
 * `detail-layout.tsx`'s header asked for a lint ("renders `MobileBottomNav`
 * => its `main` has `pb-16`"). This is stronger: the spacer is DERIVED from
 * the same boolean that renders the nav, in one table row, so the pair cannot
 * disagree — and outside this directory the chrome components are not
 * importable at all (`no-restricted-imports` in the app's eslint config), so no
 * module can mint a twelfth composition.
 *
 * ## Why it lives HERE and not in `src/app/`
 *
 * Because `root-layout.tsx` renders it, and the generated client route table
 * — which imports `root-layout` — is compiled by the SAME Bazel target as
 * every other `src/app/*` module (`src_app_entry`, tsbuildgen's Layer 7). A
 * `PageShell.tsx` in `src/app/` therefore closes a target CYCLE:
 * `src_app_root_layout` -> `src_app_entry` -> `src_app_root_layout`. It is
 * also the honest address: this element type IS "page chrome" in the app's
 * own DAG (`src/shared/components/{layout,realtime}/**` = `infra`), and what
 * this file does is compose page chrome. The `shell` still owns the
 * composition — `root-layout.tsx` renders exactly this one component.
 *
 * ## Where the answer comes from
 *
 * `handle.chrome`, emitted STATICALLY on the route object by `webroutesgen`
 * from `web/manifests/routes.yaml`'s `chrome:` — on the layout GROUP, which
 * covers its subtree, and on the leaves that override it. Static, not through
 * the `lazy()` module: React Router would take a `handle` from a lazy module,
 * but then the shell would not know the chrome until that chunk resolved — a
 * chrome FLASH on first paint and a different answer on the server than in
 * hydration, in an app whose architecture bans viewport state in JS for
 * exactly that reason.
 *
 * ## Why the bottom edge is a table and not a judgement
 *
 * `Z_LAYERS` puts `actionBar` (z-30) UNDER `bottomNav` (z-40) deliberately —
 * an action bar must never hide the app's navigation. So a page that renders
 * both loses: the nav paints over the band, and on the listing editor that was
 * measured as `elementFromPoint` at Save's own centre hitting a nav tab. The
 * fix is not a layer swap; it is that a `focus` page renders no nav. That
 * is the `focus` row, and `page-chrome.spec.ts` asserts the pair is never
 * both visible on any representative route at either viewport.
 */
import { isPageChrome, type PageChrome } from '@/shared/lib/page-chrome_gen';
import { cn } from '@letsgethome/ui/lib/utils';
import { Outlet, useMatches } from 'react-router';

import { Footer } from './Footer';
import { MobileBottomNav } from './MobileBottomNav';
import { SiteNavbar } from './SiteNavbar';

/**
 * What a chrome kind RENDERS. Every field is a band at a page edge; `main`'s
 * classes are derived from them rather than declared beside them, which is the
 * whole mechanism — the spacer and the nav are one row.
 */
export interface ChromeSpec {
  /** Which navbar, if any. `site` is `SiteNavbar` (itself `hidden md:block`). */
  readonly navbar: 'site' | 'none';
  /** The public `Footer`, desktop only — every layout wrapped it `hidden md:block`. */
  readonly footer: boolean;
  /** The four-tab `MobileBottomNav`, mobile only (it is `md:hidden`). */
  readonly bottomTabs: boolean;
}

/**
 * THE table. `satisfies Record<PageChrome, ChromeSpec>` over the GENERATED
 * union, so a kind added to the manifest's vocabulary does not typecheck here
 * until someone says which bands it renders — and a kind deleted there stops
 * compiling here rather than rotting as an unreachable row.
 *
 * No two rows may be equal: a distinction the manifest can express and the
 * shell cannot honour is a declaration that lies.
 */
export const PAGE_CHROME = {
  hub: { navbar: 'site', footer: true, bottomTabs: true },
  article: { navbar: 'site', footer: true, bottomTabs: false },
  focus: { navbar: 'site', footer: false, bottomTabs: false },
  inbox: { navbar: 'site', footer: false, bottomTabs: true },
  flow: { navbar: 'none', footer: false, bottomTabs: false },
} as const satisfies Record<PageChrome, ChromeSpec>;

/**
 * `main`'s class list, DERIVED.
 *
 * `flex flex-col` on every kind, unconditionally: a `bottom`-only sticky band
 * (`StickyActionBar`) is offset upward from its FLOW position and never pushed
 * down, so on a page shorter than the viewport it needs `mt-auto` in a column
 * to sit at the bottom edge. Giving it to every kind is what lets any page
 * grow one without also having to change its layout's contract (the LET-120
 * argument, made total).
 *
 * `pb-16 md:pb-0` iff the tab bar renders: the nav is `fixed`, i.e. out of
 * flow, so without the spacer it occludes the last 64 px of the page. That
 * this reads `spec.bottomTabs` — the same value that renders the nav — is the
 * point of the table.
 */
export function pageChromeMainClassName(spec: ChromeSpec): string {
  return cn('flex min-h-screen flex-col', spec.bottomTabs && 'pb-16 md:pb-0');
}

/**
 * The DEEPEST matched route that declares a chrome.
 *
 * Deepest wins, so a leaf overrides its group (that is how `/profile` is a tab
 * root inside an otherwise chrome-less account subtree). React Router types
 * `handle` as `unknown`, so the narrowing is ours; an unrecognised value
 * THROWS rather than defaulting, because a default would render some chrome
 * for a route whose chrome nobody decided — the F4 defect with a new face.
 * `webroutesgen` refuses to emit a page whose chain declares nothing, so
 * reaching either throw means the generated table and this file disagree.
 *
 * Taken as a plain argument rather than read from a hook so both refusals are
 * assertable: a throw from a route ELEMENT is swallowed by React Router's own
 * error boundary, which would make the test observe an error page instead of
 * the exception, and the message is the whole value of the throw.
 */
export function pageChromeFromMatches(
  matches: readonly { readonly id: string; readonly handle: unknown }[]
): PageChrome {
  for (let i = matches.length - 1; i >= 0; i -= 1) {
    const match = matches[i];
    const handle: unknown = match?.handle;
    if (typeof handle === 'object' && handle !== null && 'chrome' in handle) {
      const { chrome } = handle as { chrome: unknown };
      if (isPageChrome(chrome)) return chrome;
      throw new Error(
        `PageShell: route "${match?.id ?? '?'}" declares handle.chrome=${JSON.stringify(chrome)}, which is not a page-chrome kind.`
      );
    }
  }
  throw new Error(
    'PageShell: no matched route declares handle.chrome. Every layout group in web/manifests/routes.yaml public_render must carry `chrome:`.'
  );
}

/**
 * The app shell's chrome band + the ONE `main` landmark.
 *
 * `main` is here and nowhere else — a `no-restricted-syntax` row makes a JSX
 * `<main>` outside this directory an error. Before this, eleven layouts each
 * rendered one and twenty page modules rendered a SECOND inside it, which is
 * why `web/e2e/helpers`' landmark assertion had to say `.first()` "for layouts
 * with nested mains".
 */
export function PageShell() {
  const spec = PAGE_CHROME[pageChromeFromMatches(useMatches())];
  return (
    <>
      {spec.navbar === 'site' && <SiteNavbar />}
      <main className={pageChromeMainClassName(spec)}>
        <Outlet />
      </main>
      {/* Desktop only, exactly as all eleven layouts spelled it by hand. */}
      {spec.footer && (
        <div className="hidden md:block">
          <Footer />
        </div>
      )}
      {spec.bottomTabs && <MobileBottomNav />}
    </>
  );
}
